import { useAuthStore } from '../stores'
import { useComandasStore } from '../stores/useComandasStore'
import { playNotification as playSoundFile } from './audioUtils'
import { createSocketDeduper } from './socketEventDedup'
import { reportNotifDiagnostic } from '../services/notifDiagnosticService'

// --- Audio unlock state and helpers
let audioUnlocked = false
export function isAudioUnlocked() {
  return Boolean(audioUnlocked)
}

/**
 * Try to resume a single global AudioContext (reused on window) and/or play a
 * tiny silent audio to satisfy autoplay user-gesture requirements.
 * This must be called from a user gesture (click/tap).
 */
export async function unlockAudioContext() {
  try {
    console.log('[realtimeNotifications] unlockAudioContext called — attempting resume/unlock')

    const w = window
    const AudioCtxClass = w.AudioContext || w.webkitAudioContext
    if (AudioCtxClass) {
      if (!w.__pb_audio_ctx) {
        try {
          w.__pb_audio_ctx = new AudioCtxClass()
        } catch (e) {
          // ignore creation errors
        }
      }
      const ctx = w.__pb_audio_ctx
      if (ctx && typeof ctx.resume === 'function') {
        try {
          await ctx.resume()
        } catch (e) {
          // resume may throw if already running or not allowed; continue to fallback
        }
      }
    }

    // Fallback: play a short silent audio (must exist in /public/sounds/)
    try {
      const a = new Audio('/sounds/silence.mp3')
      a.volume = 0
      await a.play().catch(() => {})
      try { a.pause() } catch (e) {}
      try { a.currentTime = 0 } catch (e) {}
    } catch (e) {
      // ignore
    }

    audioUnlocked = true
    console.log('[realtimeNotifications] audioUnlocked = true')
    window.dispatchEvent(new CustomEvent('pb:audio-unlocked'))
    return true
  } catch (err) {
    console.warn('[realtimeNotifications] unlockAudioContext failed', err)
    return false
  }
}

export function initNotificationAudio() {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[realtimeNotifications] initNotificationAudio')
  }
}

let listenersRegistered = false
let registeredSocket = null

const NOTIFY_SOCKET_EVENTS = [
  'flash-comanda',
  'cocina-comanda-actualizada',
  'cambio-mesa',
  'borrar-comanda',
  'comanda-cerrada',
  'plato-procesado',
  'comanda-pagada',
  'abrir-cajon'
]

const unbindNotificationListeners = (sock) => {
  if (!sock) return
  if (typeof sock.removeAllListeners === 'function') {
    for (const eventName of NOTIFY_SOCKET_EVENTS) {
      sock.removeAllListeners(eventName)
    }
    return
  }
  if (typeof sock.off !== 'function') return
  for (const eventName of NOTIFY_SOCKET_EVENTS) {
    sock.off(eventName)
  }
}

const socketDeduper = createSocketDeduper(2600)

/** Evita doble alerta (toast/sonido) si el mismo cierre llega más de una vez. */
const cajaFlowAlertAt = new Map()
const CAJA_FLOW_ALERT_MS = 3200
const CAJA_FLOW_MAP_MAX = 400

const tryCajaFlowAlert = (comandaId) => {
  const cid = Number(comandaId || 0)
  if (!cid) return true
  const now = Date.now()
  const prev = cajaFlowAlertAt.get(cid) || 0
  if (now - prev < CAJA_FLOW_ALERT_MS) return false
  cajaFlowAlertAt.set(cid, now)
  if (cajaFlowAlertAt.size > CAJA_FLOW_MAP_MAX) {
    for (const [k, t] of cajaFlowAlertAt) {
      if (now - t > 120000) cajaFlowAlertAt.delete(k)
    }
  }
  return true
}

const uniqueSoundKeys = new Map()
const UNIQUE_SOUND_MS = 2200

export function clearDedupRegistry() {
  uniqueSoundKeys.clear()
  cajaFlowAlertAt.clear()
  recentKdsMeseroByComanda.clear()
}

export function checkDuplicate(key) {
  const k = String(key || '')
  if (!k) return false
  const now = Date.now()
  const prev = uniqueSoundKeys.get(k) || 0
  return now - prev < UNIQUE_SOUND_MS
}

export function playUniqueNotification(key, fileName, options = {}) {
  const k = String(key || '')
  if (!k || !fileName) return false
  const now = Date.now()
  const prev = uniqueSoundKeys.get(k) || 0
  if (now - prev < UNIQUE_SOUND_MS) return false
  uniqueSoundKeys.set(k, now)
  playSoundFile(fileName, options)
  return true
}

/**
 * Desempaqueta payload enviado por el watcher (a veces viene como { payload: {...} }).
 */
export function resolveSocketPayload(raw) {
  const envelope = raw && typeof raw === 'object' ? raw : {}
  const inner = envelope.payload && typeof envelope.payload === 'object' ? envelope.payload : envelope
  return inner && typeof inner === 'object' ? inner : {}
}

/** Diagnóstico en vistas destino: acción socket + payload resuelto. */
export function logNotificationArrival(vista, accion, raw) {
  const payload = resolveSocketPayload(raw)
  console.log(`[NOTIF][${vista}]`, { accion, payload })
}


const pickSound = (payload) => String(payload?.sonido || payload?.sound || '').trim()

const isViewOwnedRoute = () => {
  try {
    const path = String(window.location.pathname || '').toLowerCase()
    return path.includes('/cocina') || path.includes('/cajero') || path.includes('/mesero')
  } catch {
    return false
  }
}

const playIfAny = (payload) => {
  if (isViewOwnedRoute()) return
  const name = pickSound(payload)
  if (!name || name.toLowerCase() === 'silence.mp3') return
  playSoundFile(name, { notificationId: payload?.event_id || payload?.notification_id || '' })
}

export function playPayloadSound(payload = {}, options = {}) {
  const name = pickSound(payload)
  if (!name || name.toLowerCase() === 'silence.mp3') return Promise.resolve(false)
  return playSoundFile(name, {
    ...options,
    notificationId: options.notificationId || payload?.event_id || payload?.notification_id || ''
  })
}

// -----------------------
// Role / view based audio filtering
// -----------------------
const ROLE_ALLOW_MAP = {
  'flash-comanda': ['COCINERO', 'COCINA', 'BARISTA', 'BARTENDER', 'ADMINISTRADOR', 'ADMIN'],
  'plato-procesado': ['MESERO'],
  'cocina-comanda-actualizada': ['COCINERO', 'COCINA', 'BARISTA', 'BARTENDER', 'ADMINISTRADOR', 'ADMIN'],
  'comanda-cerrada': ['CAJERO', 'ADMINISTRADOR', 'ADMIN'],
  'comanda-pagada': ['CAJERO', 'ADMINISTRADOR', 'ADMIN'],
  'abrir-cajon': ['CAJERO'],
  'cambio-mesa': ['COCINERO', 'COCINA', 'BARISTA', 'BARTENDER', 'CAJERO', 'ADMINISTRADOR', 'ADMIN'],
  'borrar-comanda': ['COCINERO', 'COCINA', 'BARISTA', 'BARTENDER', 'ADMINISTRADOR', 'ADMIN']
}

const normalizeRole = () => {
  try {
    return String(useAuthStore().user?.rol || '').trim().toUpperCase()
  } catch (e) {
    return ''
  }
}

const shouldPlayForEvent = (eventName, payload) => {
  try {
    const allowed = ROLE_ALLOW_MAP[eventName]
    if (!Array.isArray(allowed)) return true // no restriction

    const role = normalizeRole()
    if (role && allowed.includes(role)) return true

    // If payload explicitly targets the current user (mesero/personal), allow
    const me = getAuthUserId()
    const target = Number(payload?.id_mesero || payload?.personal_id || 0)
    if (me && target && me === target) return true

    // Fallback: try to infer from path if role is not set
    try {
      const path = String(window.location.pathname || '').toLowerCase()
      if (path.includes('/cocina') && allowed.includes('COCINERO')) return true
      if (path.includes('/mesero') && allowed.includes('MESERO')) return true
      if (path.includes('/cajero') && allowed.includes('CAJERO')) return true
    } catch (ee) {
      // noop
    }

    return false
  } catch (err) {
    return true
  }
}

const getAuthUserId = () => {
  try {
    return Number(useAuthStore().user?.id || 0)
  } catch {
    return 0
  }
}

/** Evita doble toast si el watcher emite editar-* justo después de cocina-comanda-actualizada (misma comanda). */
const recentKdsMeseroByComanda = new Map()
const KDS_MESERO_SUPPRESS_MS = 1000

const esPersonalCocinaOBar = () => {
  try {
    const r = String(useAuthStore().user?.rol || '').trim().toUpperCase()
    return r === 'COCINERO' || r === 'COCINA' || r === 'BARISTA' || r === 'BARTENDER' || r === 'ADMIN' || r === 'ADMINISTRADOR'
  } catch {
    return false
  }
}

const esCajeroOAdmin = () => {
  try {
    const r = String(useAuthStore().user?.rol || '').trim().toUpperCase()
    return r === 'CAJERO' || r === 'ADMIN' || r === 'ADMINISTRADOR'
  } catch {
    return false
  }
}

const shouldSuppressEditarAfterKds = (comandaId) => {
  if (!esPersonalCocinaOBar()) return false
  const cid = Number(comandaId || 0)
  if (!cid) return false
  const t = recentKdsMeseroByComanda.get(cid) || 0
  return Date.now() - t < KDS_MESERO_SUPPRESS_MS
}

const markKdsMeseroSignal = (comandaId) => {
  const cid = Number(comandaId || 0)
  if (!cid) return
  recentKdsMeseroByComanda.set(cid, Date.now())
  if (recentKdsMeseroByComanda.size > 200) {
    const cutoff = Date.now() - KDS_MESERO_SUPPRESS_MS * 4
    for (const [k, v] of recentKdsMeseroByComanda) {
      if (v < cutoff) recentKdsMeseroByComanda.delete(k)
    }
  }
}

const comandaIdFromPayload = (payload) =>
  Number(payload?.comanda_id || payload?.id_comanda || payload?.id || 0)

/**
 * Una sola clave de toast/sonido por comanda nueva (flash-comanda).
 */
const kitchenNewUiKey = (payload) => {
  const cid = comandaIdFromPayload(payload)
  return cid > 0 ? `kitchen-new:${cid}` : ''
}

const notifyCerradaMessage = (payload) => {
  const mesaNombre = String(payload?.nombre_mesa || payload?.mesa_nombre || '').trim()
  const mesaId = Number(payload?.mesa_id || payload?.id_mesa || 0)
  const cid = comandaIdFromPayload(payload)
  const mesaLabel = mesaNombre || (mesaId ? `Mesa ${mesaId}` : (cid ? `Comanda #${cid}` : 'Comanda'))
  return `${mesaLabel} pide cuenta`
}

export function registerNotificationListeners(socket) {
  if (!socket) return
  if (registeredSocket === socket && listenersRegistered) {
    console.warn('[realtimeNotifications] Listeners ya registrados')
    return
  }
  if (registeredSocket && registeredSocket !== socket) {
    reportNotifDiagnostic({
      level: 'warn',
      code: 'SOCKET_INSTANCE_CHANGE',
      vista: 'realtime',
      msg: 'se reenganchó a una nueva instancia de socket'
    })
    unbindNotificationListeners(registeredSocket)
  }
  listenersRegistered = true
  registeredSocket = socket
  const store = useComandasStore()

  const traceAndDedupe = (eventName, payload = {}, customKey = '') => {
    const ok = socketDeduper(eventName, payload, customKey)
    reportNotifDiagnostic({
      level: ok ? 'info' : 'warn',
      code: ok ? 'RECEIVE' : 'DEDUP_DROP',
      event: eventName,
      event_id: payload?.event_id || payload?.notification_id,
      comanda_id: comandaIdFromPayload(payload),
      vista: 'realtime',
      msg: ok ? 'evento recibido' : 'duplicado ignorado'
    })
    return ok
  }

  socket.on('flash-comanda', (raw) => {
    const payload = resolveSocketPayload(raw)
    if (!esPersonalCocinaOBar()) return
    const uiKey = kitchenNewUiKey(payload)
    if (!uiKey || !traceAndDedupe('flash-comanda', payload, uiKey)) return
    console.log('[SOCKET] flash-comanda', payload)
    markKdsMeseroSignal(comandaIdFromPayload(payload))
    const mesa = String(payload?.mesa || payload?.mesa_nombre || payload?.nombre_mesa || '').trim()
    notifyUi({
      message: mesa ? `Nueva comanda (${mesa})` : 'Nueva comanda recibida',
      type: 'success',
      duration: 4000,
    })
    if (shouldPlayForEvent('flash-comanda', payload)) {
      playIfAny(payload)
    }
  })

  socket.on('cocina-comanda-actualizada', (raw) => {
    const payload = resolveSocketPayload(raw)
    if (!esPersonalCocinaOBar()) return
    const cid = comandaIdFromPayload(payload)
    if (!cid) return
    const did = Number(payload?.detalle_id || 0)
    const dedupKey =
      String(payload?.tipo || '') === 'detalle' && did > 0 ? `kds:${cid}:d:${did}` : `kds:${cid}`
    if (!traceAndDedupe('cocina-comanda-actualizada', payload, dedupKey)) return
    console.log('[SOCKET] cocina-comanda-actualizada', payload)
    store.updateComanda(payload)
    if (shouldSuppressEditarAfterKds(cid)) return
    markKdsMeseroSignal(cid)
    const esDetalle = String(payload?.tipo || '') === 'detalle'
    notifyUi({
      message: esDetalle ? 'Producto actualizado por el mesero' : 'Pedido actualizado por el mesero',
      type: 'info',
      duration: 4000,
    })
    if (shouldPlayForEvent('cocina-comanda-actualizada', payload)) {
      playIfAny(payload)
    }
  })

  socket.on('cambio-mesa', (raw) => {
    const payload = resolveSocketPayload(raw)
    if (!traceAndDedupe('cambio-mesa', payload)) return
    console.log('[SOCKET] cambio-mesa', payload)
    const mesa = String(payload?.mesa_nombre || payload?.nombre_mesa || '').trim()
    notifyUi({
      message: mesa ? `Mesa cambiada: ${mesa}` : 'Se cambió la mesa de una comanda',
      type: 'info',
      duration: 3500,
    })
    if (shouldPlayForEvent('cambio-mesa', payload)) {
      playIfAny(payload)
    }
  })

  socket.on('borrar-comanda', (raw) => {
    const payload = resolveSocketPayload(raw)
    if (!traceAndDedupe('borrar-comanda', payload)) return
    console.log('[SOCKET] borrar-comanda', payload)
    store.removeComanda(payload)
    if (!esPersonalCocinaOBar()) return
    notifyUi({ message: 'Comanda eliminada', type: 'warning', duration: 4000 })
    if (shouldPlayForEvent('borrar-comanda', payload)) {
      playIfAny(payload)
    }
  })

  socket.on('comanda-cerrada', (raw) => {
    const payload = resolveSocketPayload(raw)
    if (!traceAndDedupe('comanda-cerrada', payload)) return
    console.log('[SOCKET] comanda-cerrada', payload)
    const cid = comandaIdFromPayload(payload)
    const exists = store.comandas.some((c) => Number(c.id) === cid)
    if (!exists) store.addComanda(payload)
    if (esCajeroOAdmin() && tryCajaFlowAlert(cid)) {
      notifyUi({ message: notifyCerradaMessage(payload), type: 'info', duration: 4200 })
    }
    if (shouldPlayForEvent('comanda-cerrada', payload)) {
      playIfAny(payload)
    }
  })

  socket.on('plato-procesado', (raw) => {
    const payload = resolveSocketPayload(raw)
    const me = getAuthUserId()
    const target = Number(payload?.id_mesero || payload?.personal_id || 0)
    if (!target || !me || target !== me) return
    const cid = comandaIdFromPayload(payload)
    const did = Number(payload?.detalle_id || 0)
    const eventId = String(payload?.event_id || payload?.notification_id || '').trim()
    const platoDedupKey = did > 0 ? `plato:${cid}:${did}` : `plato:${cid}:${eventId}`
    if (!traceAndDedupe('plato-procesado', payload, platoDedupKey)) return
    console.log('[SOCKET] plato-procesado', payload)
    store.updateComanda(payload)
    notifyUi({ message: 'Producto listo para entrega.', type: 'success', duration: 4500 })
    playIfAny(payload)
  })

  socket.on('comanda-pagada', (raw) => {
    const payload = resolveSocketPayload(raw)
    if (!traceAndDedupe('comanda-pagada', payload)) return
    console.log('[SOCKET] comanda-pagada', payload)
    store.removeComanda(payload)
    const mesa = String(payload?.mesa_nombre || payload?.nombre_mesa || '').trim()
    const cid = comandaIdFromPayload(payload)
    const me = getAuthUserId()
    const target = Number(payload?.id_mesero || payload?.personal_id || 0)
    if (esCajeroOAdmin() || (me && target && me === target)) {
      notifyUi({
        message: mesa ? `Comanda ${mesa} pagada` : (cid ? `Comanda #${cid} pagada` : 'Comanda pagada'),
        type: 'success',
        duration: 4000,
      })
    }
    if (shouldPlayForEvent('comanda-pagada', payload)) {
      playIfAny(payload)
    }
  })

  socket.on('abrir-cajon', (raw) => {
    const payload = resolveSocketPayload(raw)
    if (!traceAndDedupe('abrir-cajon', payload)) return
    console.log('[SOCKET] abrir-cajon', payload)
    if (shouldPlayForEvent('abrir-cajon', payload)) {
      playIfAny(payload)
    }
  })

  console.log('[realtimeNotifications] Listeners de notificaciones en tiempo real registrados')
}

export function notifyUi(options = {}) {
  const {
    message = '',
    type = 'info',
    duration = 3500,
    persistent = false,
  } = options
  const text = String(message ?? '').trim()
  if (!text) return
  window.dispatchEvent(new CustomEvent('pb:notify-ui', {
    detail: { message: text, type, duration, persistent },
  }))
}

/** Reproduce un sonido desde `/public/sounds/` (misma lógica que las vistas). */
export function playNotification(fileName, options = {}) {
  return playSoundFile(fileName, options)
}
